import { createAsyncThunk } from '@reduxjs/toolkit';
import jobDetailsApi from '~/apis/jobDetails';
import { CREATE_CREDIT_MEMO, CREATE_INVOICE, CREATE_JOB_TASK, GET_INVOICE_JOB_DETAILS_LIST, GET_JOB_DETAILS, GET_LIST_BUDGET_TRACKER_JOB_DETAILS, GET_LIST_CREDIT_MEMO_JOB_DETAILS, GET_OVERVIEW_ACTIVITY_FEED, GET_OVERVIEW_CUSTOMER_DETAILS, GET_OVERVIEW_JOB_DETAILS, GET_OVERVIEW_JOB_TASK_LIST, GET_OVERVIEW_WORK_ORDER_LIST, GET_PAYMENT_JOB_DETAILS_LIST, JOB_DETAILS_ADD_CONTACT, TOGGLE_ARCHIVED_BUDGET_TRACKER, UPDATE_CREDIT_MEMO, UPDATE_JOB_TASK, UPDATE_OVERVIEW_CUSTOMER_DETAILS, UPDATE_OVERVIEW_JOB_DETAILS } from '~/utils/constants/actionType';
import { IApiBaseResponse } from '~/utils/interface/common';
import {
  IBudgetTrackerJobDetailsListResponse,
  ICreditMemoJobDetailsListResponse,
  IInvoiceJobDetailsListResponse,
  IJobDetails,
  IOverviewActivityFeed,
  IOverviewCustomerDetails,
  IOverviewJobDetails,
  IOverviewJobTaskListResponse,
  IOverviewWorkOrderListResponse,
  IParamsGetInvoiceJobDetails,
  IParamsGetOverviewJobTaskList,
  IParamsGetOverviewWorkOrderList,
  IPayloadCreateCreditMemo,
  IPayloadCreateInvoice,
  IPayloadCreateJobTask,
  IPayloadUpdateOverviewCustomerDetails,
  IPayloadUpdateOverviewJobDetails,
  IPaymentJobDetailsListResponse,
} from '~/utils/interface/jobDetails';

export const handleGetJobDetails = createAsyncThunk<
  IApiBaseResponse<IJobDetails>,
  string,
  { rejectValue: { status?: number; message: string } }
>(
  GET_JOB_DETAILS,
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getJobDetails(id);
      return res.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch job details";
      return rejectWithValue({ status, message });
    }
  }
);

export const handleGetOverviewJobDetails = createAsyncThunk<IApiBaseResponse<IOverviewJobDetails>, string>(
  GET_OVERVIEW_JOB_DETAILS,
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getOverviewJobDetails(id);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleUpdateOverviewJobDetails = createAsyncThunk<IApiBaseResponse<IOverviewJobDetails>, { id: string; data: IPayloadUpdateOverviewJobDetails }>(
  UPDATE_OVERVIEW_JOB_DETAILS,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.updateOverviewJobDetails(id, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetOverviewCustomerDetails = createAsyncThunk<IApiBaseResponse<IOverviewCustomerDetails>, string>(
  GET_OVERVIEW_CUSTOMER_DETAILS,
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getOverviewCustomerDetails(id);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleUpdateOverviewCustomerDetails = createAsyncThunk<IApiBaseResponse<any>, { id: string; data: IPayloadUpdateOverviewCustomerDetails }>(
  UPDATE_OVERVIEW_CUSTOMER_DETAILS,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.updateOverviewCustomerDetails(id, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const handleGetOverviewActivityFeed = createAsyncThunk<IApiBaseResponse<IOverviewActivityFeed[]>, string>(
  GET_OVERVIEW_ACTIVITY_FEED,
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getOverviewActivityFeed(id);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetOverviewWorkOrderList = createAsyncThunk<IApiBaseResponse<IOverviewWorkOrderListResponse>, IParamsGetOverviewWorkOrderList>(
  GET_OVERVIEW_WORK_ORDER_LIST,
  async (params: IParamsGetOverviewWorkOrderList, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getOverviewWorkOrderList(params);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleJobDetailsAddContact = createAsyncThunk<IApiBaseResponse<any>, { id: string; data: IPayloadUpdateOverviewCustomerDetails }>(
  JOB_DETAILS_ADD_CONTACT,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.jobDetailsAddContact(id, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const handleGetOverviewJobTaskList = createAsyncThunk<IApiBaseResponse<IOverviewJobTaskListResponse>, { id: string; params: IParamsGetOverviewJobTaskList }>(
  GET_OVERVIEW_JOB_TASK_LIST,
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getOverviewJobTaskList(id, params);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleCreateJobTask = createAsyncThunk<IApiBaseResponse<any>, IPayloadCreateJobTask>(
  CREATE_JOB_TASK,
  async (data: IPayloadCreateJobTask, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.createJobTask(data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleUpdateJobTask = createAsyncThunk<IApiBaseResponse<any>, { id: string; data: IPayloadCreateJobTask }>(
  UPDATE_JOB_TASK,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.updateJobTask(id, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetInvoiceJobDetailsList = createAsyncThunk<IApiBaseResponse<IInvoiceJobDetailsListResponse>, { id: string; params: IParamsGetInvoiceJobDetails }>(
  GET_INVOICE_JOB_DETAILS_LIST,
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getInvoiceJobDetailsList(id, params);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleCreateInvoice = createAsyncThunk<IApiBaseResponse<any>, { jobId: string; data: IPayloadCreateInvoice }>(
  CREATE_INVOICE,
  async ({ jobId, data }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.createInvoice(jobId, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetPaymentJobDetailsList = createAsyncThunk<IApiBaseResponse<IPaymentJobDetailsListResponse>, { idJob: string }>(
  GET_PAYMENT_JOB_DETAILS_LIST,
  async ({ idJob }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getListPaymentInFinancialTab(idJob);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetListBudgetTrackerJobDetails = createAsyncThunk<IApiBaseResponse<IBudgetTrackerJobDetailsListResponse>, { idJob: string }>(
  GET_LIST_BUDGET_TRACKER_JOB_DETAILS,
  async ({ idJob }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getListBudgetTrackerJobDetails(idJob);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetListCreditMemoJobDetails = createAsyncThunk<IApiBaseResponse<ICreditMemoJobDetailsListResponse>, { idJob: string }>(
  GET_LIST_CREDIT_MEMO_JOB_DETAILS,
  async ({ idJob }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.getListCreditMemoJobDetails(idJob);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleCreateCreditMemo = createAsyncThunk<IApiBaseResponse<any>, { jobId: string; data: IPayloadCreateCreditMemo }>(
  CREATE_CREDIT_MEMO,
  async ({ jobId, data }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.createCreditMemo(jobId, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleUpdateCreditMemo = createAsyncThunk<IApiBaseResponse<any>, { jobId: string; idCreditMemo: string; data: IPayloadCreateCreditMemo }>(
  UPDATE_CREDIT_MEMO,
  async ({ jobId, idCreditMemo, data }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.updateCreditMemo(jobId, idCreditMemo, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleToggleArchivedBudgetTracker = createAsyncThunk<IApiBaseResponse<any>, { idJob: string; idBudget: string }>(
  TOGGLE_ARCHIVED_BUDGET_TRACKER,
  async ({ idJob, idBudget }, { rejectWithValue }) => {
    try {
      const res = await jobDetailsApi.toggleArchivedBudgetTracker(idJob, idBudget);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);